/** Tipos base del modelo de simulación. Este módulo no depende de Three ni de React. */

/**
 * Posición de la palanca. Los automáticos usan P·R·N·D; los manuales usan
 * R·N y marchas numéricas 1..6. Un mismo tipo evita duplicar toda la tubería.
 */
export type Gear = 'P' | 'R' | 'N' | 'D' | 1 | 2 | 3 | 4 | 5 | 6;
export type VehicleClass = 'city' | 'sedan' | 'suv' | 'van' | 'ev' | 'loaded';
export type Transmission = 'auto' | 'manual';

/** ¿Es una marcha hacia delante de caja manual? */
export function isForwardGear(g: Gear): g is 1 | 2 | 3 | 4 | 5 | 6 {
  return typeof g === 'number';
}

export interface V3 {
  x: number;
  y: number;
  z: number;
}
export interface Q4 {
  x: number;
  y: number;
  z: number;
  w: number;
}

export interface CarDims {
  lengthM: number;
  widthM: number;
  heightM: number;
  wheelbaseM: number;
  trackM: number;
  wheelRadiusM: number;
}

/** Parámetros del tren motriz manual (motor + embrague + caja en H). */
export interface ManualSpec {
  idleRpm: number;
  /** Por debajo de estas rpm con carga, el motor se cala. */
  stallRpm: number;
  redlineRpm: number;
  /** Curva de par normalizada: [rpm, fracción 0..1] ordenada por rpm. */
  torqueCurve: [number, number][];
  clutch: {
    /** Posición del pedal (0=suelto, 1=pisado) donde el embrague empieza a morder. */
    bitePoint: number;
    /** Anchura del recorrido útil alrededor del punto de fricción. */
    biteWidth: number;
    /** Par máximo transmisible con el embrague cerrado (N·m). */
    maxTorqueNm: number;
  };
  reverseRatio: number;
  /** Inercia del volante+motor (kg·m²): baja = revoluciona rápido. */
  engineInertia: number;
  /** Eje motriz (por defecto delantero). */
  drivenAxle?: 'front' | 'rear';
}

export interface CarSpec {
  id: string;
  class: VehicleClass;
  nameKey: string;
  massKg: number;
  maxPowerKw: number;
  maxTorqueNm: number;
  /** Fuerza total de frenado que el sistema puede solicitar (N). El agarre limita la real. */
  brakeForceN: number;
  /** Coeficiente de agarre base del neumático (seco = ~1.0). */
  tireGrip: number;
  centerOfGravityHeightM: number;
  dims: CarDims;
  /** Resistencia aerodinámica agrupada: F = dragCoef * v^2 (N). */
  dragCoef: number;
  /** Resistencia a la rodadura (fracción de m·g). */
  rollingResist: number;
  /** Fuerza de arrastre del convertidor de par al ralentí en D/R (N). */
  creepForceN: number;
  /** Relaciones de la caja (1ª..n), automática o manual. */
  gearRatios: number[];
  finalDrive: number;
  regenBraking?: boolean;
  /** Presente solo en coches de cambio manual. */
  manual?: ManualSpec;
  assists: {
    abs: boolean;
    tc: boolean;
    esc: boolean;
    reverseCam: boolean;
    hillHold: boolean;
  };
  /** Color de carrocería para el visual. */
  color: string;
}

export type GlanceDir = 'none' | 'left' | 'right' | 'rear';
export type LightsMode = 0 | 1 | 2; // apagadas | cruce | carretera

/** Entrada muestreada a frecuencia de simulación. Todo lo que afecta al paso físico. */
export interface InputFrame {
  throttle: number; // 0..1
  brake: number; // 0..1
  /** Pedal de embrague: 0 = suelto (embragado), 1 = pisado a fondo. */
  clutch: number;
  steer: number; // -1 (izquierda) .. +1 (derecha)
  gear: Gear; // posición de selector SOLICITADA por el conductor
  /** Llave/botón de arranque mantenido (para rearrancar tras calarse). */
  ignition: boolean;
  handbrake: boolean;
  signalLeft: boolean;
  signalRight: boolean;
  lights: LightsMode;
  horn: boolean;
  seatbelt: boolean;
  glance: GlanceDir; // comprobación de espejos activa este tick
}

export const NEUTRAL_INPUT: InputFrame = {
  throttle: 0,
  brake: 0,
  clutch: 0,
  steer: 0,
  gear: 'P',
  ignition: false,
  handbrake: false,
  signalLeft: false,
  signalRight: false,
  lights: 0,
  horn: false,
  seatbelt: false,
  glance: 'none',
};

export interface WheelSnapshot {
  /** Posición local X (± vía/2). */
  x: number;
  /** Posición local Z (± batalla/2). */
  z: number;
  /** Longitud actual de la suspensión (m). */
  suspensionM: number;
  /** Ángulo de dirección (rad). */
  steerRad: number;
  /** Ángulo de giro acumulado de la rueda (rad). */
  spinRad: number;
  inContact: boolean;
}

/** Telemetría específica del tren motriz manual (null en automáticos). */
export interface ManualSnapshot {
  engineOn: boolean;
  /** El motor de arranque está girando. */
  cranking: boolean;
  stallCount: number;
  /** Pedal de embrague (0 suelto, 1 pisado). */
  clutchPedal: number;
  /** Fracción de par transmisible por el embrague (0 abierto, 1 cerrado). */
  engagement: number;
  /** Diferencia de rpm motor↔caja mientras patina. */
  slipRpm: number;
  /** El pedal está dentro de la zona del punto de fricción. */
  atBite: boolean;
  /** Calor acumulado del embrague por patinado (0..1+). */
  clutchHeat: number;
  /** Posición de palanca solicitada (puede diferir mientras se engrana). */
  gearRequested: Gear;
  overRev: boolean;
  lugging: boolean;
  /** Indicador de cambio (GSI): conviene subir/bajar marcha. */
  shiftUpHint: boolean;
  shiftDownHint: boolean;
}

export interface CarSnapshot {
  tick: number;
  pos: V3;
  quat: Q4;
  vel: V3;
  /** Velocidad longitudinal con signo (m/s, + adelante). */
  speedMs: number;
  speedKmh: number; // valor absoluto
  gear: Gear;
  /** Marcha interna de la caja (0-based) cuando gear === 'D'. */
  ratioIndex: number;
  rpm: number;
  shifting: boolean;
  absActive: boolean;
  tcActive: boolean;
  hillHold: boolean;
  parkLock: boolean;
  handbrake: boolean;
  signalLeft: boolean;
  signalRight: boolean;
  lights: LightsMode;
  seatbelt: boolean;
  horn: boolean;
  glance: GlanceDir;
  throttle: number;
  brake: number;
  steerRad: number;
  /** Aceleración longitudinal (m/s², + adelante). */
  accelLong: number;
  /** Pendiente bajo el coche (rad, + cuesta arriba en el sentido de la marcha). */
  slopeRad: number;
  odoM: number;
  wheels: WheelSnapshot[];
  /** Luz de freno encendida (freno o regenerativa fuerte). */
  brakeLight: boolean;
  /** Telemetría manual (solo coches con spec.manual). */
  manual: ManualSnapshot | null;
}

export type GearDenyReason = 'brakeRequired' | 'moving' | 'shifting' | 'clutchRequired';

export type SimEvent =
  | { type: 'gearChanged'; gear: Gear }
  | { type: 'gearDenied'; requested: Gear; reason: GearDenyReason }
  | { type: 'collision'; tag: string; speedKmh: number }
  | { type: 'signalCancelled'; side: 'left' | 'right' }
  /** El motor se ha calado. */
  | { type: 'stall' }
  /** El motor ha arrancado (tras el starter). */
  | { type: 'engineStart' }
  /** Intento de arrancar sin pisar embrague ni estar en N. */
  | { type: 'crankBlocked' }
  /** Rascada: mover la palanca sin pisar el embrague. */
  | { type: 'gearGrind'; requested: Gear }
  /** Sobrerrégimen (p. ej. reducir a una marcha demasiado corta). */
  | { type: 'overRev' };

export const FIXED_DT = 1 / 60;
export const KMH = 3.6;
